import React, { createContext, useContext, useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Stack,
  Alert,
  AlertTitle,
  IconButton,
  Box,
  Fade,
  Zoom,
  alpha,
  useTheme
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CloseIcon from "@mui/icons-material/Close";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { TWO_FA_EXPIRED_MESSAGE } from "../api/authSessionEvents";

const ErrorDialogContext = createContext(null);

export const useErrorDialog = () => useContext(ErrorDialogContext);

export function ErrorDialogProvider({ children }) {
  const [errorInfo, setErrorInfo] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const showError = useCallback((error, fallback = "Something went wrong") => {
    let message = fallback;
    let status;

    // Handle direct string messages
    if (typeof error === "string") {
      message = error;
    }
    // Handle error objects
    else if (
      typeof error === "object" &&
      error !== null &&
      "response" in error
    ) {
      const response = error.response;
      status = response?.status;
      message = response?.data?.message || response?.statusText || fallback;
    } else if (typeof error === "object" && error !== null) {
      // Handle non-standard object errors (like Axios wrapped in another structure)
      message =
        error?.error?.response?.data?.message ||
        error?.response?.data?.message ||
        fallback;
    } else {
      // Handle completely unexpected error types
      console.error("Unknown error type:", error);
      message = fallback;
    }

    if (
      typeof message === "string" &&
      message.includes(TWO_FA_EXPIRED_MESSAGE)
    ) {
      return;
    }

    if (
      typeof message === "string" &&
      /prisma|transaction already closed|expired transaction|invalid `\w|invocation in/i.test(
        message
      )
    ) {
      message =
        "We’re unable to complete this request at the moment. Please try again later.";
    }

    setErrorInfo({ message, status });
    setIsDialogOpen(true);
  }, []);

  const handleClose = () => {
    setIsDialogOpen(false);

    // If it's a 401 error, redirect to auth page after dialog closes
    if (errorInfo?.status === 401) {
      // Small delay to ensure dialog closes smoothly
      setTimeout(() => {
        window.location.href = "/auth?request_type=login";
      }, 300);
    }
  };

  const handleExited = () => {
    setErrorInfo(null);
  };

  return (
    <ErrorDialogContext.Provider value={showError}>
      {children}
      <ErrorDialog
        open={isDialogOpen}
        onClose={handleClose}
        onExited={handleExited}
        info={errorInfo}
      />
    </ErrorDialogContext.Provider>
  );
}

function getErrorMessage(status) {
  const httpErrorStatusMessages = {
    400: "Something went wrong with your request. Please check and try again.",
    401: "Your session has expired. Please log in again.",
    403: "You don’t have access to this.",
    404: "We couldn’t find what you’re looking for.",
    409: "This action can’t be completed because a similar item already exists.",
    422: "Some of the information entered is invalid. Please check and try again.",
    429: "You’re doing that too quickly. Please wait a moment and try again.",
    500: "Something went wrong on our side. Please try again later.",
    501: "This feature isn’t available yet.",
    503: "The service is temporarily unavailable. Please try again later."
  };

  return (
    httpErrorStatusMessages[status] ||
    "An unexpected error occurred. Please try again."
  );
}

function ErrorDialog({ open, onClose, onExited, info }) {
  const theme = useTheme();

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClose();
    }
  };

  const isForbiddenError = info?.status === 403;
  const isUnauthorizedError = info?.status === 401;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      onExited={onExited}
      maxWidth='sm'
      fullWidth
      onKeyDown={handleKeyDown}
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.12)",
          overflow: "hidden"
        }
      }}
    >
      <Fade in={open} timeout={300}>
        <Box>
          <DialogTitle
            sx={{
              bgcolor: isForbiddenError
                ? "warning.main"
                : isUnauthorizedError
                  ? "error.main"
                  : "error.main",
              color: "primary.contrastText",
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              pr: 1,
              py: 2,
              position: "relative",
              "&::after": {
                content: '""',
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: "4px",
                background: isForbiddenError
                  ? "linear-gradient(90deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0.1) 100%)"
                  : "linear-gradient(90deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0.1) 100%)"
              }
            }}
          >
            <Stack direction='row' alignItems='center' spacing={1.5}>
              <Zoom in={open} style={{ transitionDelay: "100ms" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    bgcolor: "rgba(255, 255, 255, 0.2)"
                  }}
                >
                  {isForbiddenError ? (
                    <LockOutlinedIcon color='white' />
                  ) : (
                    <ErrorOutlineIcon color='white' />
                  )}
                </Box>
              </Zoom>
              <Typography
                variant='h6'
                color='white'
                fontWeight='600'
                sx={{
                  letterSpacing: "0.5px",
                  fontSize: "1.1rem"
                }}
              >
                {isForbiddenError
                  ? "Access Denied"
                  : isUnauthorizedError
                    ? "Session Expired"
                    : "Something Went Wrong"}
              </Typography>
            </Stack>
            <IconButton
              onClick={onClose}
              size='small'
              sx={{
                color: "white",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  transform: "rotate(90deg)"
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent
            dividers
            sx={{
              p: 3,
              bgcolor: "background.paper",
              position: "relative",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "1px",
                background: isForbiddenError
                  ? "linear-gradient(90deg, rgba(237, 108, 2, 0.1) 0%, rgba(237, 108, 2, 0.2) 50%, rgba(237, 108, 2, 0.1) 100%)"
                  : "linear-gradient(90deg, rgba(211, 47, 47, 0.1) 0%, rgba(211, 47, 47, 0.2) 50%, rgba(211, 47, 47, 0.1) 100%)"
              }
            }}
          >
            <Fade in={open} timeout={500} style={{ transitionDelay: "200ms" }}>
              <Stack spacing={2}>
                <Typography
                  variant='body1'
                  sx={{
                    color: "text.primary",
                    lineHeight: 1.6,
                    fontSize: "1rem",
                    fontWeight: 500,
                    whiteSpace: "pre-line"
                  }}
                >
                  {info?.message
                    ? info.message
                    : getErrorMessage(info?.status) ||
                      "An unexpected error occurred. Please try again."}
                </Typography>
                {isForbiddenError && (
                  <Alert
                    severity='warning'
                    sx={{
                      mt: 2,
                      bgcolor: alpha(theme.palette.warning.light, 0.25),
                      "& .MuiAlert-icon": {
                        color: "warning.dark"
                      }
                    }}
                  >
                    <AlertTitle>What you can do:</AlertTitle>
                    <Typography variant='body2' sx={{ mt: 0.5 }}>
                      • Contact your administrator to request access
                      <br />
                      • Check if you're logged in with the correct account
                      <br />• Verify your user permissions
                    </Typography>
                  </Alert>
                )}
                {isUnauthorizedError && (
                  <Alert
                    severity='info'
                    sx={{
                      mt: 2,
                      bgcolor: "info.light",
                      "& .MuiAlert-icon": {
                        color: "info.dark"
                      }
                    }}
                  >
                    <AlertTitle>Next Steps:</AlertTitle>
                    <Typography variant='body2' sx={{ mt: 0.5 }}>
                      • You will be redirected to the login page
                      <br />
                      • Please log in again to continue
                      <br />• Your session data will be cleared for security
                    </Typography>
                  </Alert>
                )}
              </Stack>
            </Fade>
          </DialogContent>
        </Box>
      </Fade>
    </Dialog>
  );
}
